import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { FormGroup, Validators } from '@angular/forms';
import { debounceTime, filter, Subject, takeUntil } from 'rxjs';
import { AlertService } from 'src/app/components/alert/alert.service';
import { IIbanObject } from 'src/app/modules/athora/athora.constants';
import { PolicyService } from 'src/app/modules/policy/policy.service';
import { ITableKey } from 'src/app/modules/shared/components/basic-table/basic-table.constants';
import { IFormKey } from 'src/app/modules/shared/models/forms';
import { IPolicyAsset } from 'src/app/modules/shared/models/policy.model';
import { LoggerService } from 'src/app/services/logger.service';
import { SpinnerService } from 'src/app/services/spinner.service';
import { CommonUtils } from 'src/app/utils/CommonUtils';
import { IInvestmentLine } from '../../../shared/models/policy.model';
import {
  ADDITIONAL_PAYMENTS_ASSETS_TABLE_TABLE_KEYS,
  payment_form_keys,
  preset_payment_form_keys,
  PREV_ADDITIONAL_PAYMENTS_ASSETS_TABLE_TABLE_KEYS,
  prev_payment_form_keys,
} from './additional-payment.constants';
import moment = require('moment');

@Component({
  selector: 'app-additional-payment-isv',
  templateUrl: './additional-payment.component.html',
  styleUrl: './additional-payment.component.scss',
})
export class AdditionalPaymentComponent
  implements OnInit, OnChanges, OnDestroy
{
  @Input() form: FormGroup;
  @Input() ibanList: IIbanObject[] = [];
  @Input('assets')
  set _assets(val: IPolicyAsset[]) {
    if (val) {
      this.assets = val;
      if (this.arePresetsAvailable) {
        this.arePresetsValid.emit(this.assets);
      }
    }
  }
  @Input() isReadOnly: boolean;
  @Input() debitDate: string;
  @Input() lines: IInvestmentLine[];
  @Output() arePresetsValid: EventEmitter<IPolicyAsset[]> =
    new EventEmitter<IPolicyAsset[]>();

  public assets: IPolicyAsset[] = [];
  public keys: IFormKey[];
  public tableKeys: ITableKey[];
  destroy$ = new Subject();

  constructor(
    private readonly _policyService: PolicyService,
    private readonly _spinnerService: SpinnerService,
    private readonly _alertService: AlertService
  ) {}

  ngOnInit(): void {
    if (!this.isPolicyPrevidenziale) {
      this.form
        .get('debitDate')
        ?.setValue(this.debitDate || moment().format('YYYY-MM-DD'));
      if (this.arePresetsAvailable) {
        if (!this.form.valid && !this.isReadOnly) {
          this._assets = [];
        } else {
          this.retrieveFunds();
        }
        this.form
          .get('additionalPaymentLine')
          ?.setValidators(Validators.required);
        if (!this.form.get('additionalPaymentLine')?.value) {
          this.form
            .get('additionalPaymentLine')
            ?.setValue(this.lines.find((line) => line.selected === true).code);
        }

        this.form
          .get('amount')
          ?.valueChanges.pipe(debounceTime(500), takeUntil(this.destroy$))
          .subscribe({
            next: (value) => {
              if (value) {
                this.retrieveFunds();
              } else {
                this._assets = [];
              }
            },
          });

        this.form
          .get('additionalPaymentLine')
          ?.valueChanges.pipe(filter(Boolean), takeUntil(this.destroy$))
          .subscribe({
            next: (value) => {
              if (this.form.get('amount')?.value) {
                this.retrieveFunds();
              }
            },
          });
      }
    }
    if (CommonUtils.isVoid(this.assets) || this.assets.length === 0) {
      this.keys = this.keys.filter((key) => key.controlName !== 'type');
      this.keys?.forEach((key) => {
        key.css = 'col-4';
      });
    }
    this.tableKeys = this.isPolicyPrevidenziale
      ? CommonUtils.assign(PREV_ADDITIONAL_PAYMENTS_ASSETS_TABLE_TABLE_KEYS)
      : CommonUtils.assign(ADDITIONAL_PAYMENTS_ASSETS_TABLE_TABLE_KEYS);
  }

  ngOnChanges(): void {
    this.keys = this.isPolicyPrevidenziale
      ? CommonUtils.assign(prev_payment_form_keys)
      : CommonUtils.assign(
          this.arePresetsAvailable
            ? preset_payment_form_keys
            : payment_form_keys
        );

    this.keys = CommonUtils.populateIbanOptions(this.keys, this.ibanList);
    if (this.arePresetsAvailable) {
      const options = this.lines.map((line) => ({
        key: line.code,
        value: line.description,
      }));
      this.keys.forEach((key) => {
        if (key.controlName === 'additionalPaymentLine') {
          key.options = options;
        }
      });
    }
  }

  retrieveFunds(): void {
    const contractNumber = this._policyService.contractNumber;
    const operationCode = this._policyService.operationId;
    const lineCode = this.form.get('additionalPaymentLine')?.value;
    const value = this.form.get('amount')?.value;
    const rawValue = value?.replace(/\./g, '')?.replace(',', '.');
    if (contractNumber && operationCode && rawValue && lineCode) {
      this._spinnerService.show();
      this._policyService
        .retrieveFunds(contractNumber, operationCode, rawValue, lineCode)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (funds: any) => {
            this._assets = funds.activeFunds.map((fund) => ({
              ...fund,
              amount: parseFloat(fund.amount).toLocaleString('it-IT', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }),
              fundAmount: parseFloat(fund.fundAmount).toLocaleString('it-IT', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }),
            }));
            this._spinnerService.hide();
          },
          error: (error) => {
            LoggerService.logError(this.constructor.name, 'retrieveFunds', {
              error,
            });
            this._alertService.openErrorMessage(
              error.error?.response?.description ||
                'Errore in fase di recupero dei fondi',
              'retrieveFunds',
              error
            );
            this._assets = [];
            this._spinnerService.hide();
          },
        });
    }
  }

  get showFunds(): boolean {
    if (this.isPolicyPrevidenziale) {
      return true;
    }
    return this.assets && this.assets.length > 0;
  }

  get isPolicyPrevidenziale(): boolean {
    return this._policyService.isDetailPrevidenziale;
  }

  get arePresetsAvailable(): boolean {
    return this.lines && this.lines.length > 0;
  }

  get searchFundsButtonDisabled(): boolean {
    const value = this.form.get('amount')?.value;
    const rawValue = value?.replace(/\./g, '')?.replace(',', '.');
    return (
      !this.form.get('additionalPaymentLine')?.value ||
      !this.form.get('amount')?.value ||
      isNaN(rawValue)
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next(null);
    this.destroy$.complete();
  }
}
